"use client";

import classNames from "classnames";
import Link from "next/link";
import { FC, useEffect, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";

import AutogeneratedCards from "@/components/AutogeneratedCarousel/AutogeneratedCards";
import { AutogeneratedCarouselCard, AutogeneratedCarouselCardGroup, AutogeneratedCarouselEntry, AutogeneratedCarouselProps, CardType } from "@/types";

const showTabs = (groups: AutogeneratedCarouselCardGroup[], type: string): boolean => {
  let res = false;
  switch (type) {
    case CardType.EDITORIAL || type !== CardType.ARTICLE:
      res = false;
      break;
    default:
      res = groups?.length > 1;
      break;
  }
  return res;
};

function getStructuredData(entry: AutogeneratedCarouselEntry) {
  if (typeof window !== "undefined") {
    let cards: AutogeneratedCarouselCard[] = [];
    entry?.groups?.forEach((group) => {
      group?.cards?.forEach((card) => {
        cards?.push(card);
      });
    });

    if (entry?.type === CardType["UNIT-TYPE"] && cards?.length > 0) {
      return {
        "@context": "http://schema.org",
        "@type": "ItemList",
        itemListElement: cards.map((card, index) => {
          return {
            "@type": "ListItem",
            position: index + 1,
            name: card.title,
            url: window?.location?.origin + card.cta?.link?.url,
          };
        }),
      };
    } else if (entry.type === CardType.PROJECT && cards.length > 0) {
      return {
        "@context": "http://schema.org",
        "@type": "ItemList",
        itemListElement: cards.map((card, index) => {
          return {
            "@type": "ListItem",
            position: index + 1,
            name: card.title,
            url: window?.location?.origin + card.cta?.link?.url,
          };
        }),
      };
    }
  }
  return false;
}

const AutogeneratedCarousel: FC<AutogeneratedCarouselProps> = ({ entry, serverSideCookieCurrency, currencyFromApiRes }) => {
  const [structuredData, setStructuredData] = useState<any>();
  useEffect(() => {
    setStructuredData(getStructuredData(entry));
  }, []);

  return (
    <>
      <section
        className={classNames("flex flex-col md:items-end md:justify-end", { "mx-auto w-22\\/24 md:w-20\\/24": entry.showAsCardList })}
        id={entry.type === CardType["UNIT-TYPE"] ? "APL-component" : ""}
      >
        {entry?.title && entry?.title !== "" ? (
          <div className="w-full">
            <div
              className={`flex items-center ${entry?.variant ? "ml-m md:ml-[calc(100%_*_2_/_24)]" : entry.showAsCardList ? "" : "justify-center"} gap-l md:justify-normal ${entry?.variant ? "md:w-4/5" : "w-full"}`}
            >
              <h2
                className={`accent-font accent-title accent-color mt-l font-primary uppercase ${entry?.variant ? "w-3/4" : ""} ${!entry.cta ? "mx-auto text-center" : ""} ${entry.showAsCardList ? "text-m md:text-l md:px-xs" : "md:mx-auto md:text-xl text-l"} md:mb-l md:mt-xxl`}
              >
                {entry?.title}
              </h2>
              {entry?.variant && entry?.cta?.link?.url && (
                <Link href={entry.cta.link.url} className="ml-auto mt-3xl text-s font-medium underline underline-offset-4 md:mb-l md:mt-xxl">
                  {entry.cta.label}
                </Link>
              )}
            </div>
          </div>
        ) : null}
        <Tabs className="w-full md:mx-auto" focusTabOnClick={false}>
          {showTabs(entry.groups ?? [], entry?.type) && <TabList>{entry?.groups?.map((tab, index) => <Tab key={index}>{tab?.label}</Tab>)}</TabList>}
          <div className="w-full md:mx-auto md:mr-0 md:pr-0">
            <div className="mb-xl mt-l md:mt-m">
              {entry?.groups?.map((tab, index) =>
                entry?.groups && entry?.groups?.length > 1 ? (
                  <TabPanel key={index}>
                    <AutogeneratedCards
                      id={entry.id}
                      cardType={entry.type}
                      cards={tab.cards}
                      cta={entry.cta}
                      variant={entry.variant}
                      highlightProjectTag={tab.highlightProjectTag}
                      showAsCardList={entry.showAsCardList}
                      serverSideCookieCurrency={serverSideCookieCurrency}
                      currencyFromApiRes={currencyFromApiRes}
                    />
                  </TabPanel>
                ) : (
                  <AutogeneratedCards
                    id={entry.id}
                    key={index}
                    cardType={entry.type}
                    cards={tab.cards}
                    cta={entry.cta}
                    variant={entry.variant}
                    highlightProjectTag={tab.highlightProjectTag}
                    showAsCardList={entry.showAsCardList}
                    serverSideCookieCurrency={serverSideCookieCurrency}
                    currencyFromApiRes={currencyFromApiRes}
                  />
                ),
              )}
            </div>
          </div>
        </Tabs>
      </section>
      {structuredData && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />}
    </>
  );
};

export default AutogeneratedCarousel;
